import Image from '@/components/ui/image';
import { ArrowUpRight } from 'lucide-react';
import Link from '@/components/ui/link';
import React from 'react';
import {
  FaDiscord,
  FaReddit,
  FaTelegram,
  FaTiktok,
  FaTwitter,
} from 'react-icons/fa';

import { SiteStatusBadge } from '@/components/general/site-status-badge';

const navigationLinks = [
  { name: 'Home', href: '/' },
  { name: 'Discover', href: '/discover' },
  { name: 'Music', href: '/music' },
  { name: 'Now', href: '/now' },
];

const communityLinks = [
  {
    name: 'Discord',
    href: 'https://discord.gg/wjJWEtDQZS',
    icon: <FaDiscord className="h-5 w-5" />,
  },
  {
    name: 'Twitter',
    href: 'https://x.com/1AnimeApp',
    icon: <FaTwitter className="h-5 w-5" />,
  },
  {
    name: 'TikTok',
    href: 'https://www.tiktok.com/@1animeapp',
    icon: <FaTiktok className="h-5 w-5" />,
  },
  {
    name: 'Telegram',
    href: 'https://t.me/o1anime',
    icon: <FaTelegram className="h-5 w-5" />,
  },
  {
    name: 'Reddit',
    href: 'https://www.reddit.com/r/1AnimeApp',
    icon: <FaReddit className="h-5 w-5" />,
  },
];

const legalLinks = [
  { name: 'Terms of Service', href: '/terms' },
  { name: 'Privacy Policy', href: 'https://1ani.me/privacy/' },
  { name: 'DMCA', href: 'https://1ani.me/dmca' },
];

function FooterBrand(): React.JSX.Element {
  return (
    <div className="space-y-4">
      <Link href="/" className="group inline-flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-border/70 bg-card shadow-sm">
          <Image
            src="/logo.svg"
            alt="1Anime logo"
            width={26}
            height={26}
            className="h-6 w-6 object-contain"
          />
        </span>
        <span className="text-lg font-bold text-foreground transition-colors group-hover:text-primary sm:text-xl">
          1Anime
        </span>
      </Link>

      <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
        Streamlined anime discovery with curated picks, fast updates, and a
        community-first experience.
      </p>

      <SiteStatusBadge />
    </div>
  );
}

function FooterLinkGroup({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}

function FooterBottomStrip(): React.JSX.Element {
  return (
    <div className="mt-8 border-t border-border/60 pt-5">
      <div className="flex flex-col gap-2 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
        <p>&copy; {new Date().getFullYear()} 1Anime. All Rights Reserved.</p>
        <p className="max-w-2xl leading-relaxed sm:text-right">
          Disclaimer: 1Anime does not host media files. Content is provided by
          non-affiliated third-party sources.
        </p>
      </div>
    </div>
  );
}

export function Footer(): React.JSX.Element {
  return (
    <footer className="relative overflow-hidden border-t border-border/70 bg-card text-muted-foreground">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute left-[-64px] top-[-32px] h-40 w-40 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-[-48px] right-[-48px] h-48 w-48 rounded-full bg-accent/20 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
        <div className="rounded-2xl border border-border/70 bg-background/70 p-5 backdrop-blur-sm sm:p-7">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1.3fr)_1fr_1fr_1fr]">
            <FooterBrand />

            <FooterLinkGroup title="Navigation">
              <ul className="space-y-2.5">
                {navigationLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      href={link.href}
                      className="inline-flex items-center gap-1.5 text-sm transition-colors hover:text-primary"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </FooterLinkGroup>

            <FooterLinkGroup title="Community">
              <ul className="space-y-2.5">
                {communityLinks.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center gap-2 text-sm transition-colors hover:text-primary"
                    >
                      {link.icon}
                      <span>{link.name}</span>
                      <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
                    </a>
                  </li>
                ))}
              </ul>
            </FooterLinkGroup>

            <FooterLinkGroup title="Legal">
              <ul className="space-y-2.5">
                {legalLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      href={link.href}
                      className="inline-flex items-center gap-1.5 text-sm transition-colors hover:text-primary"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </FooterLinkGroup>
          </div>

          <FooterBottomStrip />
        </div>
      </div>
    </footer>
  );
}
